import { useState, useEffect, useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import { get } from 'lodash';
import { Spin, message } from 'antd';
import { LuX, LuMapPin, LuBriefcase, LuUpload, LuTrash2 } from 'react-icons/lu';
import { motion } from 'motion/react';
import {
	ml,
	parse,
	uploadToS3,
	fetchModernFormQuestions,
	decorateInternalJobLink,
} from 'src/_shared/services/utils.js';
import FormElements, { formValues } from 'src/form-builder/FormElements.jsx';
import { darkenHex, accentRgba } from './utils.js';
import {
	GET_JOB_REFERRAL_CONTACTS,
	CREATE_CONTACT,
	UPDATE_CONTACT,
	CREATE_REFERRAL,
} from './graphql.js';

const STATIC_ELEMENTS = ['Label', 'LineBreak', 'Header', 'Paragraph'];

function collectFormData(questions) {
	const formData = [];
	let errors = '';
	const inputElements = (questions || []).filter(
		(item) => item && !STATIC_ELEMENTS.includes(item.element) && !item.static
	);
	for (const item of inputElements) {
		const stored = formValues[item.field_name];
		if (stored) {
			if (item.required && (!stored.value || stored.value === '')) {
				errors += `${item.label || 'Field'} is required. `;
			}
			formData.push({
				name: item.field_name,
				value: stored.value || '',
				question: item.label || stored.question || '',
				text: stored.text || '',
			});
		} else if (item.required) {
			errors += `${item.label || 'Field'} is required. `;
		}
	}
	return { formData, errors };
}

function InterestedModal({
	match,
	currentUser,
	allMultiLingualData,
	accentColor,
	onClose,
	onApplied,
}) {
	const client = useApolloClient();
	const job = match?.job;
	const internalJobLink = job?.internalJobLink;

	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [questions, setQuestions] = useState([]);
	const [resumeConfig, setResumeConfig] = useState({
		show: false,
		required: false,
		showEmail: false,
	});
	const [file, setFile] = useState(null);
	const [fileErrors, setFileErrors] = useState([]);

	useEffect(() => {
		document.body.style.overflow = 'hidden';
		const handleEsc = (e) => {
			if (e.key === 'Escape') onClose();
		};
		document.addEventListener('keydown', handleEsc);
		return () => {
			document.body.style.overflow = '';
			document.removeEventListener('keydown', handleEsc);
		};
	}, [onClose]);

	useEffect(() => {
		const load = async () => {
			try {
				const modernQuestions = await fetchModernFormQuestions(
					currentUser,
					'Interested',
					null,
					client
				);
				if (modernQuestions && modernQuestions.length > 0) {
					const sorted = modernQuestions
						.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
					const resumeItems = sorted.filter(
						(q) => q.sortOrder === 0 && q.isInterested
					);
					const questionItems = sorted
						.filter((q) => q.sortOrder > 0)
						.map((q) => {
							if (typeof q.questions === 'string') {
								try { return JSON.parse(q.questions); } catch { return q.questions; }
							}
							return q.questions;
						});
					setQuestions(questionItems);

					if (resumeItems.length > 0) {
						const resumeData = parse(get(resumeItems, '[0].questions'));
						setResumeConfig({
							show: !!resumeData?.['Ask for resume attachment'],
							required: !!resumeData?.['Resume required'],
							showEmail: !!resumeData?.["Show User's Email Address"],
						});
					}
				}
			} catch (error) {
				console.error('Error loading form questions:', error);
			} finally {
				setLoading(false);
			}
		};
		load();
	}, [client, currentUser]);

	const handleFileChange = useCallback((e) => {
		const selectedFile = e.target.files?.[0];
		e.target.value = '';
		if (!selectedFile) {
			setFile(null);
			setFileErrors([]);
			return;
		}
		const errors = [];
		const validTypes = [
			'application/pdf',
			'application/msword',
			'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
		];
		if (!validTypes.includes(selectedFile.type)) {
			errors.push('You can only upload doc, docx, or pdf files.');
		}
		if (selectedFile.size / 1024 / 1024 >= 2) {
			errors.push('File size must be smaller than 2MB.');
		}
		setFileErrors(errors);
		if (errors.length === 0) {
			setFile(selectedFile);
		}
	}, []);

	const handleSubmit = useCallback(async () => {
		if (!job?.id || !currentUser?.id) return;

		const { formData, errors: formErrors } = collectFormData(questions);
		if (formErrors.length > 0) {
			message.error('Please check all the required fields');
			return;
		}
		const isEmptySelect = formData.some(
			(item) =>
				item.name?.includes('dropdown') &&
				item.text === 'Select' &&
				item.value === '0'
		);
		if (isEmptySelect) {
			message.error('Please check all the required fields');
			return;
		}
		if (resumeConfig.required && !file) {
			message.error('Please upload your resume.');
			return;
		}

		try {
			setSubmitting(true);

			// Check if already referred
			const { data: jobData } = await client.query({
				query: GET_JOB_REFERRAL_CONTACTS,
				variables: { id: job.id },
				fetchPolicy: 'network-only',
			});
			const existingReferrals = get(jobData, 'getJob.referrals', []);
			const alreadyReferred = existingReferrals.some(
				(r) =>
					(get(r, 'contact.emailAddress') || '').toLowerCase() ===
					(currentUser.emailAddress || '').toLowerCase()
			);

			if (alreadyReferred) {
				if (internalJobLink) {
					window.open(
						decorateInternalJobLink(internalJobLink, currentUser),
						'_blank'
					);
				} else {
					message.info("You've already applied to this position.");
				}
				onClose();
				return;
			}

			// Find or create contact
			let contactId = null;
			const contacts = currentUser.contacts || [];
			const matchingContact = contacts.find(
				(c) =>
					c?.emailAddress?.toLowerCase() ===
					currentUser.emailAddress?.toLowerCase()
			);

			if (matchingContact) {
				contactId = matchingContact.id;
			} else {
				const { data: contactData } = await client.mutate({
					mutation: CREATE_CONTACT,
					variables: {
						input: {
							firstName: currentUser.firstName,
							lastName: currentUser.lastName,
							emailAddress: (currentUser.emailAddress || '').toLowerCase(),
							userId: currentUser.id,
							companyId: currentUser.companyId,
							importMethod: 'email',
						},
					},
				});
				contactId = get(contactData, 'createContact.id');
			}

			// Upload resume if provided
			if (file && contactId) {
				const timestamp = `${new Date().getHours()}-${new Date().getMinutes()}-${new Date().getSeconds()}`;
				const contactResume = {
					bucket: 'erin-documents',
					key: `resumes/${contactId}/${timestamp}-${file.name}`,
					region: 'us-east-2',
				};
				await uploadToS3(file, contactResume.key, contactResume.bucket);
				await client.mutate({
					mutation: UPDATE_CONTACT,
					variables: { input: { id: contactId, contactResume } },
				});
			}

			// Create referral
			await client.mutate({
				mutation: CREATE_REFERRAL,
				variables: {
					input: {
						companyId: currentUser.companyId,
						contactId,
						userId: currentUser.id,
						jobId: job.id,
						status: 'accepted',
						note: null,
						message: null,
						referralSource: job.isGeneralReferral ? 'general' : 'direct',
						referralType: 'self',
						interestedQuestionsData:
							formData.length > 0 ? JSON.stringify(formData) : null,
					},
				},
			});

			message.success(
				ml('Application submitted!', currentUser, allMultiLingualData)
			);

			if (internalJobLink) {
				window.open(
					decorateInternalJobLink(internalJobLink, currentUser),
					'_blank'
				);
			}

			onApplied?.(job.id);
			onClose();
		} catch (error) {
			console.error('Error submitting application:', error);
			message.error('Failed to submit application');
		} finally {
			setSubmitting(false);
		}
	}, [
		client,
		currentUser,
		job,
		questions,
		file,
		resumeConfig.required,
		internalJobLink,
		allMultiLingualData,
		onClose,
		onApplied,
	]);

	const location = job?.location;
	const locationStr =
		typeof location === 'object' && location
			? location.isRemote
				? 'Remote'
				: [location.city, location.state].filter(Boolean).join(', ')
			: typeof location === 'string'
				? location
				: '';
	const departmentName = job?.department?.name;

	const modalTxt = internalJobLink
		? 'You will be directed to our job site to apply for this position.'
		: 'Let us know that you are interested in this position and we will be in touch.';
	const submitLabel = internalJobLink ? 'Apply On Job Site' : 'Submit Me For This Position';

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center p-4"
			onClick={onClose}
		>
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				exit={{ opacity: 0 }}
				transition={{ duration: 0.3 }}
				className="fixed inset-0"
				style={{
					background: 'rgba(15,23,42,0.5)',
					backdropFilter: 'blur(4px)',
				}}
			/>

			<motion.div
				initial={{ opacity: 0, scale: 0.95 }}
				animate={{ opacity: 1, scale: 1 }}
				exit={{ opacity: 0, scale: 0.95 }}
				transition={{ duration: 0.3, ease: 'easeOut' }}
				className="relative flex w-full flex-col overflow-hidden rounded-2xl bg-white"
				style={{
					maxWidth: 560,
					maxHeight: '90vh',
					boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
				}}
				onClick={(e) => e.stopPropagation()}
			>
				<div
					className="flex items-center justify-between p-6"
					style={{ paddingBottom: 0 }}
				>
					<div style={{ width: 32 }} />
					<h2
						style={{
							color: '#1e293b',
							fontSize: 18,
							fontWeight: 600,
							margin: 0,
							flex: 1,
							textAlign: 'center',
						}}
					>
						{ml('Interested?', currentUser, allMultiLingualData)}
					</h2>
					<button
						type="button"
						onClick={onClose}
						style={{
							width: 32,
							height: 32,
							borderRadius: 12,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							padding: 0,
							lineHeight: 0,
							color: '#475569',
							background: 'none',
							border: 'none',
							cursor: 'pointer',
							transition: 'all 0.2s',
						}}
						onMouseOver={(e) => {
							e.currentTarget.style.color = '#475569';
							e.currentTarget.style.background = '#f1f5f9';
						}}
						onMouseOut={(e) => {
							e.currentTarget.style.color = '#475569';
							e.currentTarget.style.background = 'none';
						}}
					>
						<LuX size={14} />
					</button>
				</div>

				<div
					className="overflow-y-auto p-6"
					style={{
						scrollbarWidth: 'thin',
						scrollbarColor: '#cbd5e1 transparent',
					}}
				>
					<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
						<p
							style={{
								fontSize: 13,
								color: '#475569',
								margin: 0,
								textAlign: 'center',
							}}
						>
							{ml(modalTxt, currentUser, allMultiLingualData)}
						</p>

						{/* Job info card */}
						<div
							className="rounded-xl"
							style={{
								padding: 14,
								background: '#f0fdf4',
								border: '1px solid #86efac',
							}}
						>
							<h3
								style={{
									fontSize: 15,
									fontWeight: 600,
									color: '#1e293b',
									margin: 0,
									marginBottom: 6,
								}}
							>
								{job?.title}
							</h3>
							<div
								className="flex flex-wrap items-center gap-3"
								style={{ fontSize: 12, color: '#475569' }}
							>
								{departmentName && (
									<span className="flex items-center gap-1">
										<LuBriefcase size={11} />
										{departmentName}
									</span>
								)}
								{locationStr && (
									<span className="flex items-center gap-1">
										<LuMapPin size={11} />
										{locationStr}
									</span>
								)}
							</div>
							{job?.externalJobId && (
								<p
									style={{
										fontSize: 12,
										color: '#475569',
										margin: '6px 0 0',
									}}
								>
									ID#: <span style={{ fontWeight: 500 }}>{job.externalJobId}</span>
								</p>
							)}
						</div>

						{loading ? (
							<div
								className="flex items-center justify-center"
								style={{ padding: '16px 0' }}
							>
								<Spin size="small" />
							</div>
						) : (
							<>
								{/* Form questions */}
								{questions.length > 0 && (
									<div className="flex flex-col gap-3">
										{questions.map((item, index) => {
											if (!item) return null;
											const Element = FormElements[item.element];
											if (!Element) return null;
											if (STATIC_ELEMENTS.includes(item.element)) {
												return (
													<Element key={index} mutable data={item} />
												);
											}
											return (
												<div key={index}>
													<Element mutable data={item} />
												</div>
											);
										})}
									</div>
								)}

								{/* Resume upload */}
								{resumeConfig.show && (
									<div>
										{!file ? (
											<label
												className="inline-flex cursor-pointer items-center gap-2 rounded-xl"
												style={{
													padding: '12px 16px',
													border: `1px dashed ${accentRgba(accentColor, 0.35)}`,
													background: accentRgba(accentColor, 0.04),
													fontSize: 13,
													color: accentColor,
													fontWeight: 500,
													whiteSpace: 'nowrap',
												}}
											>
												<LuUpload size={14} style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', marginRight: 6, position: 'relative', top: -1 }} />
												Attach a resume
												<span
													style={{
														color: '#475569',
														fontWeight: 400,
														fontSize: 12,
														marginLeft: 2,
													}}
												>
													({resumeConfig.required ? 'required' : 'optional'})
												</span>
												<input
													type="file"
													accept=".doc,.docx,application/msword,.pdf,application/pdf"
													onChange={handleFileChange}
													style={{ display: 'none' }}
												/>
											</label>
										) : (
											<div
												className="flex items-center justify-between rounded-xl"
												style={{
													padding: '10px 14px',
													background: '#f8fafc',
													border: '1px solid #e2e8f0',
													fontSize: 13,
													color: '#334155',
												}}
											>
												<span
													style={{
														overflow: 'hidden',
														textOverflow: 'ellipsis',
														whiteSpace: 'nowrap',
													}}
												>
													{file.name}
												</span>
												<button
													type="button"
													onClick={() => {
														setFile(null);
														setFileErrors([]);
													}}
													style={{
														background: 'none',
														border: 'none',
														color: '#dc2626',
														cursor: 'pointer',
														padding: 0,
														flexShrink: 0,
														marginLeft: 8,
													}}
												>
													<LuTrash2 size={14} />
												</button>
											</div>
										)}
										{fileErrors.length > 0 && (
											<p
												style={{
													fontSize: 12,
													color: '#dc2626',
													margin: '4px 0 0',
												}}
											>
												{fileErrors.join(' ')}
											</p>
										)}
									</div>
								)}

								{/* Show email */}
								{resumeConfig.showEmail && (
									<p
										style={{
											fontSize: 12,
											color: '#475569',
											margin: 0,
											fontStyle: 'italic',
										}}
									>
										You must apply using this email address:{' '}
										<strong>{currentUser?.emailAddress}</strong>
									</p>
								)}
							</>
						)}

						{/* Action buttons */}
						<div className="flex gap-3 pt-2">
							<button
								type="button"
								onClick={onClose}
								disabled={submitting}
								style={{
									flex: 1,
									paddingTop: 10,
									paddingBottom: 10,
									borderRadius: '0.75rem',
									fontSize: 14,
									fontWeight: 600,
									background: '#f1f5f9',
									color: '#475569',
									border: 'none',
									cursor: 'pointer',
									transition: 'background 0.2s',
								}}
								onMouseOver={(e) => {
									e.currentTarget.style.background = '#e2e8f0';
								}}
								onMouseOut={(e) => {
									e.currentTarget.style.background = '#f1f5f9';
								}}
							>
								{ml('Cancel', currentUser, allMultiLingualData)}
							</button>
							<button
								type="button"
								onClick={handleSubmit}
								disabled={submitting || loading}
								style={{
									flex: 1,
									paddingTop: 10,
									paddingBottom: 10,
									borderRadius: '0.75rem',
									fontSize: 14,
									fontWeight: 600,
									color: '#fff',
									background: accentColor,
									border: 'none',
									cursor: submitting ? 'wait' : 'pointer',
									boxShadow: `0 1px 3px ${accentRgba(accentColor, 0.3)}`,
									transition: 'all 0.2s',
									opacity: submitting ? 0.7 : 1,
								}}
								onMouseOver={(e) => {
									if (!submitting)
										e.currentTarget.style.background = darkenHex(accentColor);
								}}
								onMouseOut={(e) => {
									if (!submitting)
										e.currentTarget.style.background = accentColor;
								}}
							>
								{submitting ? (
									<Spin size="small" style={{ color: '#fff' }} />
								) : (
									ml(submitLabel, currentUser, allMultiLingualData)
								)}
							</button>
						</div>
					</div>
				</div>
			</motion.div>
		</div>
	);
}

export default InterestedModal;
